import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled, { css } from "styled-components";

import { sentenceCase } from "src/library/utils/string-manipulations";
import { Button } from "src/library/components/Button";
import { ListActionsList } from "src/core/lists/components/ListActionsList";
import ListNotes from "src/core/lists/components/ListNotes";
import ListProductLine from "src/core/lists/components/ListProductLine";
import ListProducts from "src/core/lists/components/ListProducts";
import ListInfoSidebar from "src/core/lists/components/ListInfoSidebar/ListInfoSidebar";
import ListScheduleWrapper from "src/core/lists/components/ListInfoTop/ListScheduleWrapper";
import ScheduleWarningModal from "src/core/schedules/components/ScheduleWarningModal";
import { IScheduleFormData } from "src/core/schedules/components/ScheduleForm/ScheduleEditForm";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  listsApi,
  ListPreRequestUpdate
} from "src/core/lists/api/lists.api";
import {
  CreateScheduleParams,
  UpdateScheduleParams,
  SchedulePreRequestUpdate,
  ScheduleStatus,
  useScheduleApi
} from "src/core/schedules/api/schedules.api";
import type { IStore } from "src/core/shared/store";
import useList from "src/core/lists/hooks/useList";
import { PageProps } from "src/pages/Router/routes";
import useCanSubmitStandingOrder from "src/core/lists/hooks/useCanSubmitStandingOrder";
import { IProduct } from "src/core/products/api/products.api";
import useOrderingNotAvailableForDay from "src/core/orders/hooks/validation/useOrderingAvailableForDay";
import RelatedProductSplit from "src/core/orders/components/RelatedProductSplit";
import { updateListLine } from "src/core/lists/store/lists.actions";
import { RapidOrderEntry } from "src/core/products/components/RapidOrderEntry/RapidOrderEntry";
import { useDrop } from "src/core/drops/hooks";

/**
 * Main "Products" View
 *
 * @returns
 */
const ViewListPage: React.ReactFCWithChildren<PageProps<unknown, {listId: string;}>> = props => {
  const {
    match, history, terminology, location
  } = props;

  const [ { drop } ] = useDrop();
  const [ scheduleViewSetting, setScheduleViewSetting ] = React.useState<"orders" | "schedule" | null>(null);
  const [ hasSetLocationProps, setHasSetLocationProps ] = React.useState(false);
  const [ listActionType, setListActionType ] = React.useState<"cancel" | "save">();
  const [ deleteListResponse, deleteList ] = listsApi<{message: string}>("DELETE_LIST");
  const [ getScheduleResponse, getSchedule ] = useScheduleApi("GET_SCHEDULE");
  const ordersValidatingTimeout = React.useRef<NodeJS.Timeout | number | boolean>(false);
  const canSubmitStandingOrder = useCanSubmitStandingOrder();
  const [ createScheduleResponse, createSchedule ] = useScheduleApi("CREATE_SCHEDULE");
  const [ updateScheduleResponse, updateSchedule ] = useScheduleApi("UPDATE_SCHEDULE");
  const [ suspendScheduleResponse, suspendSchedule ] = useScheduleApi("SUSPEND_SCHEDULE");
  const [ reactivateScheduleResponse, reactivateSchedule ] = useScheduleApi("REACTIVATE_SCHEDULE");
  const [ preReUpdateList, setPreReUpdateList ] = listsApi<ListPreRequestUpdate>("PRE_UPDATE_LIST");
  const [ scheduleUpdateWarningModal, setScheduleUpdateWarningModal ] = React.useState<SchedulePreRequestUpdate | null>(null);
  const orderingNotAvailableForDay = useOrderingNotAvailableForDay(drop);
  const listId = match ? parseInt(match.params.listId, 0) : 0;
  const dispatch = useDispatch();

  const {
    currentRole, currentCustomer, orderStatuses, erp, installedModules, productRelatedGroups
  } = useMappedState(({ global, customers }: IStore) => ({
    currentRole: global.role,
    currentCustomer: customers.customer,
    orderStatuses: global.settings ? global.settings.orderStatuses : [],
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings ? global.settings.installedModules || [] : [],
    productRelatedGroups: global?.settings?.productRelatedGroups || []
  }));

  const hasPriceChangeModule = installedModules.includes("price-change");

  const [
    {
      isLoading: isLoadingList, currentList, isSavingList
    },
    {
      saveList, setListMode, refreshList, setList
    }
  ] = useList(
    listId,
    null,
    true
  );

  const canSaveList = canSubmitStandingOrder(currentList.data);

  React.useEffect(() => {
    return () => {
      clearInterval(ordersValidatingTimeout.current as number);
      ordersValidatingTimeout.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (currentList.data) {
      if (getScheduleResponse.data) {
        setList({
          ...currentList,
          data: {
            ...currentList.data,
            schedule: getScheduleResponse.data
          },
          saved: true
        });

        // clear the interval so we stop getting the schedule
        if (getScheduleResponse.data.validating === false) {
          clearInterval(ordersValidatingTimeout.current as number);
          ordersValidatingTimeout.current = false;
          refreshList();
        }
      }
    }
  }, [ getScheduleResponse ]);

  const handleSaveList = () => {
    if (currentList.data) {
      setListActionType("save");
      setPreReUpdateList({ listId });
    }
  };

  const primaryAction = () => {
    if (currentList.data && drop) {
      if ((currentList.data.schedule && currentList.data.schedule.validating) || isSavingList) {
        return (
          <Button disabled>
            Validating...
          </Button>
        );
      }

      if (currentList.mode === "edit") {
        const disableSave = currentList.data && !currentList.data.schedule ? false : canSaveList !== null;

        return (
          <Button
            loading={isSavingList || preReUpdateList.pending}
            disabled={isLoadingList || currentList.saved || isSavingList || disableSave}
            onClick={() => handleSaveList()}
          >
            Done
          </Button>
        );
      } else if (currentList.mode === "view" && currentRole && drop && erp) {
        return (
          <ListActionsList
            role={currentRole}
            terminology={terminology}
            erp={erp}
          />
        );
      }
    }

    return null;
  };

  const handleSetSchedule = React.useCallback((form: IScheduleFormData) => {
    if (!form.schduleType) {
      return toast.error("Missing schedule type.");
    }

    if (form.startDate && form.monthlyFrequency) {
      if (currentList.mode === "edit") {
        saveList();
      }

      if (currentList.data && currentList.data.schedule) {
        if (currentList.data.schedule.id) {
          // updating existing schedule
          const schedule: UpdateScheduleParams = {
            ...currentList.data.schedule,
            scheduleDays: form.scheduleDays,
            endDate: form.endDate,
            startDate: form.startDate,
            bankHolidaysDelivery: 1,
            monthlyFrequency: form.monthlyFrequency,
            status: ScheduleStatus.Active,
            type: form.schduleType
          };

          updateSchedule(schedule);
        }
      } else {
        // Create new schedule
        const scheduleData: CreateScheduleParams = {
          scheduleDays: form.scheduleDays,
          listId,
          endDate: form.endDate || null,
          startDate: form.startDate,
          bankHolidaysDelivery: 1,
          monthlyFrequency: form.monthlyFrequency,
          status: ScheduleStatus.Active,
          validating: false,
          type: form.schduleType
        };

        createSchedule(scheduleData);
      }
    }

    return;
  }, [
    currentList,
    createSchedule,
    updateSchedule,
    listId
  ]);

  React.useEffect(() => {
    if (preReUpdateList.data) {
      if (listActionType === "save") {
        if (preReUpdateList.data.toUpdate === 0) {
          saveList();
        } else {
          setScheduleUpdateWarningModal({ affectedOrders: preReUpdateList.data.toUpdate });
        }
      }

      if (listActionType === "cancel") {
        if (preReUpdateList.data.toUpdate === 0) {
          deleteList(listId);
        } else {
          setScheduleUpdateWarningModal({ toCancel: preReUpdateList.data.toUpdate });
        }
      }
    }

    if (preReUpdateList.error) {
      toast.error(`Could not process ${terminology.list.singular.text} action, please try again later - ${preReUpdateList.error.message}`);
    }
  }, [ preReUpdateList ]);

  const handleConfirmWarning = () => {
    if (listActionType === "cancel") {
      deleteList(listId);
    }

    if (listActionType === "save") {
      saveList();
    }

    setListActionType(undefined);
  };

  React.useEffect(() => {
    if (currentList === null) {
      setHasSetLocationProps(false);
    }

    if (location.state && location.state.edit) {
      if (currentList.data) {
        if (currentList.data.id === listId) {
          if (!hasSetLocationProps) {
            const setModeFromLocation = setListMode("edit");

            if (setModeFromLocation) {
              setHasSetLocationProps(true);
            }
          }
        }
      }
    }

    if (currentList.data && currentList.data.schedule?.id && currentList.data.schedule.validating && ordersValidatingTimeout.current === false) {
      getSchedule({ scheduleId: currentList.data.schedule.id });

      ordersValidatingTimeout.current = setInterval(() => {
        if (currentList.data?.schedule?.id) {
          getSchedule({ scheduleId: currentList.data.schedule.id });
        }
      }, 7000);
    }
  }, [ currentList, getSchedule ]);

  React.useEffect(() => {
    if (deleteListResponse.data) {
      if (currentRole && currentRole.type === "admin") {
        if (currentCustomer && drop) {
          history.push(`/admin/customers/${currentCustomer.id}/drops/${drop.id}/lists`);
        } else {
          history.push("/admin/customers");
        }
      } else {
        history.push("/lists");
      }
    }

    if (deleteListResponse.error) {
      toast.error(`Could not cancel ${terminology.list.singular.text}, please try again later - ${deleteListResponse.error.message}`);
    }
  }, [ deleteListResponse ]);

  React.useEffect(() => {
    if (updateScheduleResponse.data) {
      if (currentList.data) {
        setList({
          ...currentList,
          data: {
            ...currentList.data,
            schedule: updateScheduleResponse.data
          },
          saved: true
        });
      }

      toast.success(
        sentenceCase(
          `${terminology.schedule.singular.text} updated succesfully. ${
            terminology.order.plural.text
          } are now validating, we will let you know when this is complete!`
        )
      );
      setScheduleViewSetting("orders");
      refreshList();
    }

    if (updateScheduleResponse.error) {
      toast.error(`Could not update ${terminology.schedule.singular.text}, please try again later - ${updateScheduleResponse.error.message}`);
    }
  }, [ updateScheduleResponse ]);

  React.useEffect(() => {
    if (createScheduleResponse.data) {
      setScheduleViewSetting("orders");

      toast.success(
        sentenceCase(
          `${terminology.schedule.singular.text} created succesfully. ${sentenceCase(
            terminology.order.plural.text
          )} are now validating, we will let you know when this is complete!`
        )
      );
      refreshList();
    }

    if (createScheduleResponse.error) {
      toast.error(`Could not create ${terminology.schedule.singular.text}, please try again later - ${createScheduleResponse.error.message}`);
    }
  }, [ createScheduleResponse ]);

  React.useEffect(() => {
    if (suspendScheduleResponse.data) {
      setScheduleViewSetting("orders");

      toast.success(
        sentenceCase(
          `${terminology.schedule.singular.text} suspended succesfully. ${sentenceCase(
            terminology.order.plural.text
          )} are now validating, we will let you know when this is complete!`
        )
      );
      refreshList();
    }

    if (createScheduleResponse.error) {
      toast.error(`Could not suspend ${terminology.schedule.singular.text}, please try again later - ${createScheduleResponse.error.message}`);
    }
  }, [ suspendScheduleResponse ]);

  React.useEffect(() => {
    if (reactivateScheduleResponse.data) {
      setScheduleViewSetting("orders");

      toast.success(
        sentenceCase(
          `${terminology.schedule.singular.text} reactivated succesfully. ${
            terminology.order.plural.text
          } are now validating, we will let you know when this is complete!`
        )
      );
      refreshList();
    }

    if (createScheduleResponse.error) {
      toast.error(`Could not reactivate ${terminology.schedule.singular.text}, please try again later - ${createScheduleResponse.error.message}`);
    }
  }, [ reactivateScheduleResponse ]);

  const scheduleLoading =
    updateScheduleResponse.pending || createScheduleResponse.pending || reactivateScheduleResponse.pending || suspendScheduleResponse.pending;

  const onQuickAddItem = React.useCallback(
    (product: IProduct | null, newQuantity: number) => {
      if (currentList.data && product) {
        if (orderingNotAvailableForDay(null, product) === null) {
          dispatch(updateListLine(product, newQuantity));
        }
      }
    },
    [
      currentList,
      dispatch,
      orderingNotAvailableForDay
    ]
  );

  return (
    <Layout
      {...props}
      title={`${currentList.data && currentList.data.name ? currentList.data.name : "List details"}`}
    >
      <ScheduleWarningModal
        data={scheduleUpdateWarningModal}
        handleDismiss={() => setScheduleUpdateWarningModal(null)}
        onConfirm={() => handleConfirmWarning()}
      />

      <>
        <MainContentWrapper fill>

          {currentList.mode === "edit" && (
            <RapidOrderEntry />
          )}

          {currentList.mode === "edit" && currentList.data && currentList.data.schedule && (
            <ErrorNotice
              type="error"
              message={canSaveList}
            />
          )}

          {drop && currentList.data && currentRole && (
            <>
              {hasPriceChangeModule && (
                <ErrorNotice
                  type="warn"
                  message="* Prices displayed are at the time of ordering. Prices on orders generated with a future delivery date may differ."
                />
              )}

              <ListScheduleWrapper
                list={currentList.data}
                scheduleViewSetting={scheduleViewSetting}
                orderStatuses={orderStatuses}
                terminology={terminology}
                drop={drop}
                isScheduleLoading={scheduleLoading}
                schedule={currentList.data && currentList.data.schedule ? currentList.data.schedule : null}
                data={currentList.data}
                handleSetSchedule={form => handleSetSchedule(form)}
                scheduleErrors={currentList ? currentList.data.errors : undefined}
                handleSuspendSchedule={id => {
                  suspendSchedule({ scheduleId: id });
                }}
                handleReactivateSchedule={id => {
                  reactivateSchedule({ scheduleId: id });
                }}
                isLoading={isLoadingList}
                isEditingList={currentList.mode === "edit"}
              />

              <ListNotes
                list={currentList.data}
                terminology={terminology}
              />

              {installedModules.includes("product-related-group") ? (
                <RelatedProductSplit
                  orderLines={currentList.data.listLines}
                  OrderProductLine={listLine => {
                    return (
                      <ListProductLine
                        installedModules={installedModules}
                        key={listLine.productId}
                        mode={currentList.mode}
                        listLine={listLine}
                        drop={drop}
                        schedule={currentList.data ? currentList.data.schedule : null}
                      />
                    );
                  }}
                  productRelatedGroups={productRelatedGroups}
                />
              ) : (
                <ListProducts
                  list={currentList.data}
                  ListProductLine={listLine => {
                    return (
                      <ListProductLine
                        installedModules={installedModules}
                        key={listLine.productId}
                        mode={currentList.mode}
                        listLine={listLine}
                        drop={drop}
                        schedule={currentList.data ? currentList.data.schedule : null}
                      />
                    );
                  }}
                />
              )}
            </>
          )}
        </MainContentWrapper>

        {drop && (
          <InfoBarWrapper>
            <ListInfoSidebar
              scheduleViewSetting={scheduleViewSetting}
              orderStatuses={orderStatuses}
              viewMode={currentList.mode}
              primaryAction={primaryAction()}
              terminology={terminology}
              isLoading={isLoadingList}
              setPONumber={poNumber => currentList.data && setList({
                ...currentList,
                saved: false,
                data: {
                  ...currentList.data,
                  customerReference: poNumber
                }
              })}
              editListName={listName => currentList.data && setList({
                ...currentList,
                saved: false,
                data: {
                  ...currentList.data,
                  name: listName
                }
              })}
            />
          </InfoBarWrapper>
        )}
      </>
    </Layout>
  );
};

const OrderQuickProductAddWrapper = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.md};
  `}
`;

export default ViewListPage;
